import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, XCircle, Clock, AlertTriangle, ChevronRight } from 'lucide-react';
import './RefundPolicy.css';

export function RefundPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="refund-page">
      {/* Ambient backgrounds */}
      <div className="refund-bg">
        <div className="refund-bg__orb refund-bg__orb--1" />
        <div className="refund-bg__orb refund-bg__orb--2" />
        <div className="refund-bg__grid" />
      </div>

      <div className="refund-container">
        {/* Breadcrumbs */}
        <div className="refund-breadcrumbs">
          <Link to="/">Home</Link>
          <ChevronRight size={12} />
          <span>Refund & Cancellation Policy</span>
        </div>

        {/* Header */}
        <header className="refund-header">
          <span className="refund-eyebrow">Financial Protocols</span>
          <h1 className="refund-title">Refund & <span className="refund-title__accent">Cancellation</span></h1>
          <p className="refund-subtitle">
            Effective Date: June 1, 2026. Complete transparency on ticket cancellations, reschedules, and financial transaction reversals.
          </p>
        </header>

        {/* Core pillars */}
        <div className="refund-highlights">
          <div className="refund-card">
            <div className="refund-card__icon"><XCircle size={24} /></div>
            <h3>Bookings are Final</h3>
            <p>Once processed, ticket purchases cannot be manually cancelled or swapped by the buyer.</p>
          </div>
          <div className="refund-card">
            <div className="refund-card__icon"><RefreshCw size={24} /></div>
            <h3>Cancellation Coverage</h3>
            <p>100% automated refunds if an event is officially cancelled by our production team.</p>
          </div>
          <div className="refund-card">
            <div className="refund-card__icon"><Clock size={24} /></div>
            <h3>5-7 Day Reversals</h3>
            <p>Verified refund reversals are credited directly back to your original payment source.</p>
          </div>
        </div>

        {/* Layout */}
        <div className="refund-layout">
          {/* Sidebar */}
          <aside className="refund-sidebar">
            <div className="refund-sidebar__sticky">
              <h3>Table of Contents</h3>
              <nav>
                <a href="#general">1. General Purchase Rules</a>
                <a href="#cancellation">2. Event Cancellations</a>
                <a href="#rescheduling">3. Event Rescheduling</a>
                <a href="#processing">4. Refund Processing</a>
                <a href="#force-majeure">5. Force Majeure</a>
                <a href="#support">6. Assistance Hotline</a>
              </nav>
            </div>
          </aside>

          {/* Policy content */}
          <article className="refund-content">
            <section id="general">
              <h2>1. General Purchase Rules</h2>
              <p>
                Luminar Entertainments produces highly complex, large-scale spectacles with fixed stadium and venue capacities. Consequently, **all ticket sales executed via this portal are strictly final, non-refundable, and non-exchangeable**.
              </p>
              <p>
                Before finalizing your checkout, please review your chosen ticket classes, event titles, dates, quantities, and transaction totals carefully. We do not provide refunds or swaps in the case of:
              </p>
              <ul>
                <li>Change of mind or schedule conflicts after checkout.</li>
                <li>Accidental ticket selections or typos in quantities.</li>
                <li>Inability to attend due to personal emergencies or transit delays.</li>
                <li>Eviction from the venue due to violation of venue code of conduct rules.</li>
              </ul>
            </section>

            <section id="cancellation">
              <h2>2. Event Cancellations</h2>
              <p>
                In the rare event that Luminar Entertainments officially cancels an entire event due to production constraints, artist emergencies, or technical breakdowns:
              </p>
              <p>
                We will initiate a **100% complete refund** of the face value of the tickets. The refund will be automatically triggered inside our database and routed back through the secure payment gateway. Buyers do not need to file manual claims; the process is fully automated.
              </p>
            </section>

            <section id="rescheduling">
              <h2>3. Event Rescheduling</h2>
              <p>
                If an event is rescheduled (such as postponing a stadium concert to a future date due to extreme weather warnings):
              </p>
              <p>
                Your original generated PDF ticket remains fully valid and will grant entry to the new date automatically. 
              </p>
              <p>
                If you are unable to attend the newly scheduled date, you will have a **48-hour window** from the official rescheduling announcement to request a full refund. You can submit your booking reference code to our support hotline, and the transaction value will be reversed in full.
              </p>
            </section>

            <section id="processing">
              <h2>4. Refund Processing</h2>
              <p>
                All authorized refunds (whether due to cancellation or approved reschedule claims) are processed directly back to the original funding source utilized during checkout:
              </p>
              <ul>
                <li><strong>Reversal Target:</strong> Refunds are sent back to the exact credit card, debit card, net banking account, or UPI handle that funded the payment checkout. We cannot issue refunds in cash or to alternative banking accounts due to anti-money laundering (AML) protocols.</li>
                <li><strong>Timeline:</strong> Once initiated, the bank reversal typically reflects inside your account within **5 to 7 business days**, depending entirely on standard banking clearing cycles.</li>
              </ul>
              <blockquote>
                <AlertTriangle size={16} />
                <strong>Dispute Warning:</strong> Please do not file chargeback disputes with your credit card issuer for valid reschedules. Reach our support hotline first so we can reverse the funds directly, which is significantly faster.
              </blockquote>
            </section>

            <section id="force-majeure">
              <h2>5. Force Majeure</h2>
              <p>
                Luminar Entertainments is not liable, and will not issue refunds, for event cancellations, postponements, or venue restrictions resulting from **Force Majeure events**. 
              </p>
              <p>
                These include, but are not limited to: acts of God, earthquakes, severe storms, flooding, war, civil unrest, labor strikes, government health lockdowns, pandemics, municipal security threats, or sudden power grid failure.
              </p>
            </section>

            <section id="support">
              <h2>6. Assistance Hotline</h2>
              <p>
                If you have any transaction-related queries, want to submit a rescheduling refund claim, or have not received your automated reversal within 7 business days, please contact our support desk:
              </p>
              <div className="refund-contact-details">
                <p><strong>Support Ticket Desk:</strong> <a href="mailto:luminarentertainments123@gmail.com">luminarentertainments123@gmail.com</a></p>
                <p><strong>Hotline:</strong> +91 81290 77431</p>
                <p><strong>Mailing Address:</strong> Luminar entertainments private limited, 3rd Floor, Royal Plaza, Bank junction, Periyar Nagar, Aluva, Kerala 683101</p>
              </div>
            </section>
          </article>
        </div>
      </div>
    </div>
  );
}
